import React, { useEffect, createRef, memo } from 'react';
import { TimelineLite } from 'gsap';
import { Col } from 'reactstrap';
import classNames from 'classnames';
import Splitter from 'split-html-to-chars';
import get from 'lodash/get';
import { useStaticQuery, graphql } from 'gatsby';
import BackgroundImage from 'gatsby-background-image';

import { Title } from 'components/typography';
import ScrollAnimation from 'components/scrollAnimation';
import { ANIMATION_SETTINGS } from 'config';

import LandingSectionButton from './LandingSectionButton';
import {
  landingSection,
  landingCaptionContainer,
  landingSectionButtonsContainer,
} from './scss/LandingSection.module.scss';

const LandingSection = () => {
  const bgImageData = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "landing/landing-image.png" }) {
        childImageSharp {
          fluid(quality: 90, maxWidth: 1000) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);
  const titleRef = createRef();
  const timeline = new TimelineLite();
  const navButtons = [
    { label: 'impact', href: '/updates#impact' },
    { label: 'xperts', href: '/about#leadership' },
    { label: 'markets', href: '/market' },
  ];

  useEffect(() => {
    const title = titleRef.current;
    const outerHTML = get(title, 'outerHTML', '');
    title.outerHTML = Splitter(outerHTML, '<span class="titleChar">$</span>');

    timeline
      .to('.introTitle', 0, { opacity: 1 })
      .staggerFromTo(
        '.titleChar',
        0.1,
        ANIMATION_SETTINGS.SLIDE_UP.FROM,
        ANIMATION_SETTINGS.SLIDE_UP.TO,
        0.015
      )
      .staggerFromTo(
        '.landing-button',
        ANIMATION_SETTINGS.SLIDE_UP.DURATION,
        ANIMATION_SETTINGS.SLIDE_UP.FROM,
        ANIMATION_SETTINGS.SLIDE_UP.TO,
        0.08,
        '-=0.2'
      )
      .staggerTo(
        '.retractedText .titleChar',
        0.4,
        { color: '#A7181C', delay: 0.8 },
        0.03,
      )
      .staggerTo(
        '.retractedText .titleChar',
        0.4,
        {
          width: 0,
          display: 'none',
          delay: 2.5
        },
        -0.04,
        '-=1'
      )
      .delay(0.2);
  }, []);

  return (
    <ScrollAnimation
      triggerElement=".landingSection"
      triggerHook="onEnter"
      tween={timeline}
      offset={0}
    >
      <BackgroundImage
        Tag="div"
        fluid={bgImageData.file.childImageSharp.fluid}
        className={classNames('landingSection', landingSection)}
        name="landingSection"
      >
        <Col md="12" xs="12" xl="10">
          <span
            ref={titleRef}
            className={classNames(landingCaptionContainer, 'introTitle')}
          >
            <Title
              value={
                <>
                  <span>
                    {`Because we `}
                    <br className="d-sm-none d-xs-block" />
                  </span>
                  <span className="retractedText">
                    {` are crazy enough`}
                    <br className="disposableBreakChar titleChar" />
                    {` to think we`}
                  </span>
                  <span className="survivingText">
                    {` can help Africa`}
                    <br className="d-none d-xl-block" />
                    {` feed itself.`}
                  </span>
                </>
              }
              size="xxl"
            />
          </span>
        </Col>
        <Col md="12" xs="12">
          <div className={landingSectionButtonsContainer}>
            {navButtons.map(({ label, ...props }) => (
              <LandingSectionButton
                key={label}
                {...props}
                className="landing-button"
              >
                {label}
              </LandingSectionButton>
            ))}
          </div>
        </Col>
      </BackgroundImage>
    </ScrollAnimation>
  );
};

export default memo(LandingSection);
